import React, { useEffect, useState, useMemo } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { useHistory } from "react-router-dom";
import { Col, Card, Row, Badge, Dropdown, Button } from 'react-bootstrap';
import moment from 'moment-timezone';
import { DataTable, DataTableLoading, DataTableRecordsNotFound } from '../widgets/DataTable';
import { CardContainer } from '../widgets/CardContainer';
import { getHardwareList, hardwareList, getLatestSensorList } from '../../actions/hardware';
import { SearchInput, ToggleSwitch } from '../widgets';
import { connectSocket } from '../../actions/socket';
import HardwareRow from './HardwareRow';
import { userConfiguration, updateConfiguration, getUserConfiguration } from '../../actions/user';
import { SubNav } from '../layout/submenu';
import { searchWord } from '../../helpers/common';
import { Workbook } from 'react-excel-workbook';
import { DISPLAY_DATE_FORMAT2, NOT_APPLICABLE } from '../../constants';
import { toDisplayTimeString } from '../../helpers/moment-ex';

const HardwareList = (props) => {
    const { isRequesting, hardwareList, sensorListById, actions, configuration: { autoRefresh }, farmId } = props;

    let timerId = null;
    const history = useHistory();
    const [selected, setSelected] = useState({ farm: [], networkStatus: [], canCode: [], canInterface: [], battery: [] });
    const [searchText, setSearchText] = useState("");
    const [farmSearchText, setFarmSearchText] = useState("");
    const [canCodeSearchText, setCanCodeSearchText] = useState("");
    const searchInput = React.createRef();
    const [sort, setSort] = useState(false);

    useEffect(() => {
        props.actions.getUserConfiguration("autoRefresh", { configuration_name: "AutoRefresh" });

        if (!window.socketClient) actions.connectSocket();
        getHardwareList();

        return () => actions.hardwareList();
    }, []);

    useEffect(() => {
        clearTimer();
        if (autoRefresh) timerId = setInterval(() => getHardwareList(), 5 * 60 * 1000);

        return clearTimer;
    }, [autoRefresh]);

    const clearTimer = () => {
        if (timerId) clearInterval(timerId);
        timerId = null;
    }

    const getHardwareList = () => {
        actions.getHardwareList((list) => {
            const egList = [];
            list.forEach(item => {
                if (item.current_hardware_info && item.current_hardware_info.equipment_group_uuid)
                    egList.push({ equipment_group_uuid: item.current_hardware_info.equipment_group_uuid });
            })
            actions.getLatestSensorList(egList)
        });
    };

    const handleRefreshToggle = (e) => {
        const isChecked = e.target.checked;
        const cb = (isSaved) => !isSaved && props.actions.userConfiguration("autoRefresh", { device_console: !isChecked });

        props.actions.userConfiguration("autoRefresh", { device_console: isChecked });
        props.actions.updateConfiguration({
            configuration_name: "AutoRefresh",
            configuration: { device_console: isChecked }
        }, cb)

        if (isChecked) handleRefresh();
    }

    const handleRefresh = () => getHardwareList();

    const getFarmFilterList = () => {
        const list = [];
        hardwareList.forEach(item => {
            item.farm_list.forEach(item => {
                if (!list.some(item2 => item2.farm_uuid === item.farm_uuid))
                    list.push({ farm_uuid: item.farm_uuid, farm_name: item.farm_name })
            });
        })
        return list;
    }

    const farmFilterList = useMemo(getFarmFilterList, [hardwareList]);

    const onSelect = (type, id, checked) => {
        const selectedItems = [...selected[type]];
        if (checked) selectedItems.push(id);
        else selectedItems.splice(selectedItems.indexOf(id), 1);
        setSelected({ ...selected, [type]: selectedItems });
    };

    const getFilteredHardwareList = () => {
        let list = hardwareList;

        const isSearchTextMatched = value => searchWord(value, searchText);
        list = list.filter(e => {
            if (searchText.trim().length > 0) {
                return isSearchTextMatched(e.hardware_name) ||
                    e.farm_list.some(item => isSearchTextMatched(item.farm_name)) ||
                    isSearchTextMatched(e.current_hardware_info.equipment_group_name) ||
                    isSearchTextMatched(e.current_hardware_info.can_code) ||
                    isSearchTextMatched(e.current_hardware_info.imei);
            } else return true;
        });

        list = list.filter(item => {
            if (selected.farm.length !== 0 && !item.farm_list.some(item1 => selected.farm.indexOf(item1.farm_uuid) >= 0))
                return false;

            const currentCanCode = (item.current_hardware_info || {}).can_code;
            if (selected.canCode.length !== 0
                && !(selected.canCode.indexOf(currentCanCode) >= 0 || (selected.canCode.includes(NOT_APPLICABLE) && !currentCanCode)))
                return false;

            if (Object.keys(sensorListById).length > 0) {
                const egId = item.current_hardware_info ? item.current_hardware_info.equipment_group_uuid : null;
                const sensorsInfo = sensorListById && sensorListById[egId] ? sensorListById[egId] : null;

                if (selected.networkStatus.length !== 0) {
                    if (!sensorsInfo || !sensorsInfo.qConnection || sensorsInfo.qConnection.connected === null) return false;
                    if (!selected.networkStatus.some(item => item === (sensorsInfo.qConnection.connected ? 1 : 2))) return false;
                }

                if (selected.canInterface.length !== 0) {
                    if (!sensorsInfo || !sensorsInfo.canConnection || sensorsInfo.canConnection.state === null) return false;
                    const status = sensorsInfo.canConnection.state;
                    if (!selected.canInterface.some(item => item === status)) return false;
                }

                if (selected.battery.length !== 0) {
                    if (!sensorsInfo || !sensorsInfo.battery) return false;
                    const { percent } = sensorsInfo.battery;
                    if (!percent) return false;

                    const isMatched = selected.battery.some(item => {
                        switch (item) {
                            case 1: return percent < 25;
                            case 2: return percent >= 25 && percent < 50;
                            case 3: return percent >= 50 && percent < 75;
                            case 4: return percent >= 75 && percent <= 100;
                            default: return true;
                        }
                    });

                    if (!isMatched) return false;
                }
            }
            return true;
        });
        return list;
    }

    let filteredHardwareList = useMemo(getFilteredHardwareList, [hardwareList, sensorListById, searchText, selected, sort]);

    const fileName = `DeviceConsol_${moment.utc().format(DISPLAY_DATE_FORMAT2)}.xlsx`;

    const getWorkSheets = () => {
        const header = [
            { key: "id", label: "#" },
            { key: "hardware_name", label: "Device Name" },
            { key: "farms", label: "Farms" },
            { key: "installed_in", label: "Installed In" },
            { key: "app_version", label: "App Version" },
            { key: "network_status", label: "Network Link Status" },
            { key: "network_link_time", label: "Network Link Up/Down Time" },
            { key: "can_interface", label: "CAN Interface" },
            { key: "battery_level", label: "Battery Level" }
        ];

        const body = filteredHardwareList.map((item, index) => {
            const egId = item.current_hardware_info ? item.current_hardware_info.equipment_group_uuid : null;
            const egName = item.current_hardware_info ? item.current_hardware_info.equipment_group_name : "";

            const farms = item.farm_list.map(farmItem => {
                return farmItem.is_current_active_farm ? `${farmItem.farm_name} (Active)` : farmItem.farm_name;
            });

            const sensorsInfo = sensorListById && sensorListById[egId] ? sensorListById[egId] : null;

            let networkStatus = NOT_APPLICABLE;
            let upTime = NOT_APPLICABLE;
            let canInteface = NOT_APPLICABLE;
            let batteryText = "";

            if (sensorsInfo) {
                if (sensorsInfo.qConnection.connected) networkStatus = "Connected";
                else if (sensorsInfo.qConnection.connected === false) networkStatus = "Disconnected";

                if (sensorsInfo.qConnection.upTime !== null)
                    upTime = toDisplayTimeString(sensorsInfo.qConnection.upTime);

                const status = sensorsInfo.canConnection.state;
                if (status === 1) canInteface = "Disconnected";
                else if (status === 2) canInteface = "Needs Attention";
                else if (status === 3) canInteface = "Connected";


                const { state, percent } = sensorsInfo.battery;

                if (percent !== null) batteryText = `${percent}%`;
                if (state !== null) {
                    let stateDesc = null;
                    if (state === 1) stateDesc = "Charging";
                    else if (state === 2) stateDesc = "Discharging";

                    if (stateDesc) batteryText += batteryText ? ` (${stateDesc})` : stateDesc;
                }
            }

            return ({
                id: index,
                hardware_name: item.hardware_name,
                farms: farms.join(', '),
                installed_in: egName,
                app_version: item.current_hardware_info.app_version,
                network_status: networkStatus,
                network_link_time: upTime,
                can_interface: canInteface,
                battery_level: batteryText
            })
        });

        return <Workbook.Sheet data={body} name={"Hardwares"}>
            {header.map((item, i) => <Workbook.Column key={i} value={item.key} label={item.label} />)}
        </Workbook.Sheet>
    }

    const farmDropDownList = useMemo(() => {
        if (farmSearchText.trim().length > 0) return farmFilterList.filter(e => searchWord(e.farm_name, farmSearchText));
        else return farmFilterList
    }, [farmSearchText, farmFilterList])

    const canCodeDropDownList = useMemo(() => {
        const canCodeList = [...new Set((hardwareList || []).map(e => (e.current_hardware_info || {}).can_code || NOT_APPLICABLE))];
        if (canCodeSearchText.trim().length > 0) return canCodeList.filter(canCode => searchWord(canCode, canCodeSearchText));
        else return canCodeList;
    }, [canCodeSearchText, hardwareList]);

    filteredHardwareList = sort ? filteredHardwareList.sort((a, b) => a.hardware_type_name.toLowerCase().localeCompare(b.hardware_type_name.toLowerCase())) :
        filteredHardwareList;
    return <>
        <CardContainer lg={12} md={12} className="hardware-list px-3 animate animate__fadeIn">
            <Card className="card-list">
                <Card.Header className="hardware-header">
                    <Card.Title>
                        <Row className="hardware-header-item">
                            <div>Device Console</div>
                            <div className="mr-4 ml-auto">
                                <Row>
                                    <span className="label mt-1 mr-1">Auto refresh</span>
                                    <ToggleSwitch
                                        className="switch mt-1 mr-2"
                                        title={autoRefresh ? "Disable auto refresh" : "Enable auto refresh"}
                                        onChange={(e) => handleRefreshToggle(e)}
                                        checked={autoRefresh}
                                    />
                                    <span style={{ width: "20px", height: "20px" }} className="d-flex">
                                        {!autoRefresh && <i
                                            title="Refresh"
                                            className="fa fa-refresh btn-refresh ml-2 mt-1"
                                            aria-hidden="true"
                                            onClick={handleRefresh}
                                        />}
                                    </span>

                                    <span className={'split'}>
                                        <SubNav.Divider className={'splitter'} />
                                    </span>

                                    <div className="refresh-content">
                                        <Workbook filename={fileName}
                                            element={<Button size="sm" disabled={filteredHardwareList.length === 0} >
                                                <i className="fa fa-cloud-download" /> Download</Button>}>
                                            {getWorkSheets()}
                                        </Workbook>

                                    </div>

                                    <span className={'split'}>
                                        <SubNav.Divider className={'splitter'} />
                                    </span>

                                    <span className="label">
                                        <Button
                                            className='btn btn-sm'
                                            variant='danger'
                                            onClick={() => {
                                                const url = `/home/farm/${farmId}/map`;
                                                history.push(url);
                                            }}>
                                            <i className='fa fa-times-circle mr-1' />
                                            Cancel
                                        </Button>
                                    </span>
                                </Row>
                            </div>

                        </Row>
                    </Card.Title>
                </Card.Header>
                <Card.Body className="hardware-table">
                    <Row className="m-0 search-container" >
                        <SubNav.SearchBox
                            ref={searchInput}
                            placeholder="Search for device name, farm, installed in, IMEI, CAN code "
                            value={searchText}
                            onChange={e => setSearchText(e.target.value)}
                            onClear={() => { setSearchText(""); }}
                        />
                    </Row>
                    <Row className="m-0">
                        <Col className="p-0">
                            <DataTable className='tbl-fixed'>
                                <DataTable.Header>
                                    <DataTable.Row>
                                        <DataTable.Cell style={{ width: "30px" }}>#</DataTable.Cell>
                                        <DataTable.Cell style={{ width: "90px", textAlign: "center" }}>
                                            Device Name <span className="count">{hardwareList.length}</span>
                                        </DataTable.Cell>
                                        <DataTable.Cell className="cursor-pointer" style={{ width: "90px", textAlign: "center" }} onClick={() => setSort(!sort)}>
                                            Hardware Type <i className={`fa sort-direction ${sort ? "fa-caret-up" : "fa-caret-down"}`} />
                                        </DataTable.Cell>
                                        <DataTable.Cell style={{ width: "130px" }}>
                                            Farms <span className="count">{farmFilterList.length}</span>
                                            <Dropdown className="filter-btn-container">
                                                <Dropdown.Toggle className="filter-dropdown-toggle">
                                                    <i className="fa fa-filter filter-btn" title="Filter" />
                                                    {selected.farm.length > 0 && <Badge
                                                        variant="warning"
                                                        className="badge-filter-count">
                                                        {selected.farm.length}
                                                    </Badge>}
                                                </Dropdown.Toggle>
                                                <Dropdown.Menu align="right">
                                                    <div className="mx-2 mb-1" >
                                                        <SearchInput
                                                            onChange={searchText => setFarmSearchText(searchText)}
                                                        />
                                                    </div>
                                                    {farmDropDownList.map(item => {
                                                        return <div key={item.farm_uuid} className={`filter-dropdown-item-custom`}>
                                                            <label>
                                                                <span>
                                                                    <input
                                                                        type="checkbox"
                                                                        checked={selected.farm.indexOf(item.farm_uuid) >= 0}
                                                                        onChange={(e) => onSelect("farm", item.farm_uuid, e.target.checked)}
                                                                    />
                                                                </span>
                                                                <span className="item-text">{item.farm_name}</span>
                                                            </label>
                                                        </div>;
                                                    })}
                                                </Dropdown.Menu>
                                            </Dropdown>
                                        </DataTable.Cell>
                                        <DataTable.Cell style={{ width: "80px" }}>
                                            Installed In
                                            <span className="count">
                                                {hardwareList.filter(item =>
                                                    item.current_hardware_info.equipment_group_name).length}
                                            </span>
                                        </DataTable.Cell>
                                        <DataTable.Cell style={{ width: "90px" }}>IMEI</DataTable.Cell>
                                        <DataTable.Cell style={{ width: "40px" }}>App Version</DataTable.Cell>

                                        <DataTable.Cell style={{ width: "70px" }}>
                                            Network Link Status
                                            <Dropdown className="filter-btn-container">
                                                <Dropdown.Toggle className="filter-dropdown-toggle">
                                                    <i className="fa fa-filter filter-btn" title="Filter" />
                                                    {selected.networkStatus.length > 0 && <Badge
                                                        variant="warning"
                                                        className="badge-filter-count">
                                                        {selected.networkStatus.length}
                                                    </Badge>}
                                                </Dropdown.Toggle>
                                                <Dropdown.Menu align="right" className="networkStatus">
                                                    {[{ id: 1, label: "Connected" }, { id: 2, label: "Disconnected" }].map(item => {
                                                        return <div key={item.id} className={`filter-dropdown-item-custom`}>
                                                            <label>
                                                                <span>
                                                                    <input
                                                                        type="checkbox"
                                                                        checked={selected.networkStatus.indexOf(item.id) >= 0}
                                                                        onChange={(e) => onSelect("networkStatus", item.id, e.target.checked)}
                                                                    />
                                                                </span>
                                                                <span className="item-text">{item.label}</span>
                                                            </label>
                                                        </div>;
                                                    })}
                                                </Dropdown.Menu>
                                            </Dropdown>
                                        </DataTable.Cell>
                                        <DataTable.Cell style={{ width: "80px" }}>Network Link Up/Down Time</DataTable.Cell>
                                        <DataTable.Cell style={{ width: "140px" }}>
                                            CAN Code
                                            <Dropdown className="filter-btn-container">
                                                <Dropdown.Toggle className="filter-dropdown-toggle">
                                                    <i className="fa fa-filter filter-btn" title="Filter" />
                                                    {selected.canCode.length > 0 && <Badge
                                                        variant="warning"
                                                        className="badge-filter-count">
                                                        {selected.canCode.length}
                                                    </Badge>}
                                                </Dropdown.Toggle>
                                                <Dropdown.Menu align="right">
                                                    <div className="mx-2 mb-1" >
                                                        <SearchInput
                                                            onChange={searchText => setCanCodeSearchText(searchText)}
                                                        />
                                                    </div>
                                                    {canCodeDropDownList.map((canCode, index) => {
                                                        return <div key={index} className={`filter-dropdown-item-custom`}>
                                                            <label>
                                                                <span>
                                                                    <input
                                                                        type="checkbox"
                                                                        checked={selected.canCode.indexOf(canCode) >= 0}
                                                                        onChange={(e) => onSelect("canCode", canCode, e.target.checked)}
                                                                    />
                                                                </span>
                                                                <span className="item-text">{canCode}</span>
                                                            </label>
                                                        </div>;
                                                    })}
                                                </Dropdown.Menu>
                                            </Dropdown>
                                        </DataTable.Cell>
                                        <DataTable.Cell style={{ width: "80px" }}>
                                            CAN Interface
                                            <Dropdown className="filter-btn-container">
                                                <Dropdown.Toggle className="filter-dropdown-toggle">
                                                    <i className="fa fa-filter filter-btn" title="Filter" />
                                                    {selected.canInterface.length > 0 && <Badge
                                                        variant="warning"
                                                        className="badge-filter-count">
                                                        {selected.canInterface.length}
                                                    </Badge>}
                                                </Dropdown.Toggle>
                                                <Dropdown.Menu align="right" className="can-interface">
                                                    {[{ id: 3, label: "Connected" },
                                                    { id: 1, label: "Disconnected" },
                                                    { id: 2, label: "Needs Attention" }].map(item => {
                                                        return <div key={item.id} className={`filter-dropdown-item-custom`}>
                                                            <label>
                                                                <span>
                                                                    <input
                                                                        type="checkbox"
                                                                        checked={selected.canInterface.indexOf(item.id) >= 0}
                                                                        onChange={(e) => onSelect("canInterface", item.id, e.target.checked)}
                                                                    />
                                                                </span>
                                                                <span className="item-text">{item.label}</span>
                                                            </label>
                                                        </div>;
                                                    })}
                                                </Dropdown.Menu>
                                            </Dropdown>
                                        </DataTable.Cell>
                                        <DataTable.Cell style={{ width: "90px" }}>Battery Level
                                            <Dropdown className="filter-btn-container">
                                                <Dropdown.Toggle className="filter-dropdown-toggle">
                                                    <i className="fa fa-filter filter-btn" title="Filter" />
                                                    {selected.battery.length > 0 && <Badge
                                                        variant="warning"
                                                        className="badge-filter-count">
                                                        {selected.battery.length}
                                                    </Badge>}
                                                </Dropdown.Toggle>
                                                <Dropdown.Menu align="right" className="battery">
                                                    {[{ id: 1, label: "Less than 25%" },
                                                    { id: 2, label: "25% - 50%" },
                                                    { id: 3, label: "50% - 75%" },
                                                    { id: 4, label: "More than 75%" }].map(item => {
                                                        return <div key={item.id} className={`filter-dropdown-item-custom`}>
                                                            <label>
                                                                <span>
                                                                    <input
                                                                        type="checkbox"
                                                                        checked={selected.battery.indexOf(item.id) >= 0}
                                                                        onChange={(e) => onSelect("battery", item.id, e.target.checked)}
                                                                    />
                                                                </span>
                                                                <span className="item-text">{item.label}</span>
                                                            </label>
                                                        </div>;
                                                    })}
                                                </Dropdown.Menu>
                                            </Dropdown>
                                        </DataTable.Cell>
                                        <DataTable.Cell style={{ width: "40px" }}>Action</DataTable.Cell>
                                    </DataTable.Row>
                                </DataTable.Header>
                                <DataTable.Body>
                                    {(!isRequesting && filteredHardwareList.length > 0) && filteredHardwareList.map((item, i) =>
                                        <HardwareRow
                                            key={item.hardware_uuid}
                                            hardwareId={item.hardware_uuid}
                                            index={i + 1}
                                            item={item}
                                        />)}
                                </DataTable.Body>
                            </DataTable>
                            {isRequesting && <DataTableLoading />}
                            {(!isRequesting && filteredHardwareList.length === 0) && <DataTableRecordsNotFound />}
                        </Col>
                    </Row>
                </Card.Body>
            </Card>
        </CardContainer>
    </>
}

const mapStateToProps = state => ({
    farmId: state.farm.currentFarm.farm_uuid,
    isRequesting: state.hardware.hardwareList.isRequesting,
    hardwareList: state.hardware.hardwareList.payload,
    sensorListById: state.hardware.sensorListById.payload ? state.hardware.sensorListById.payload : null,
    configuration: {
        autoRefresh: state.user.configuration.autoRefresh && state.user.configuration.autoRefresh.hasOwnProperty("device_console")
            ? state.user.configuration.autoRefresh.device_console : false
    }
});

const mapDispatchToProps = dispatch => ({
    actions: bindActionCreators({
        getHardwareList, hardwareList, connectSocket, getLatestSensorList,
        userConfiguration, updateConfiguration, getUserConfiguration
    }, dispatch)
});

export default connect(mapStateToProps, mapDispatchToProps)(HardwareList);
